import React, { useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Navigate, useParams } from "react-router-dom";

import { useTheme } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import Modal from "@mui/material/Modal";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";

import NativeSelect from "@meetyoo/components/src/components/Selects/NativeSelect";
import { getSelectValues, getKeyFromSelectedValue, getSelectedValueTranslated } from "../../../constants/passCodeUtils";

import { moment } from "../../../utils/LocalizedUtils";

import { BILLING_LEDGER_ENTRIES_PATH } from "../../../constants/router";

import { requestLedgerEntry, updateLedgerEntry, deleteLedgerEntry } from "../../../actions/billing";

import { difference } from "../../../utils/objectMethods";

const LedgerValues = { nettype: { 0: "Festnetz", 1: "Mobilfunk" } };

export const BillingLedgerEntryComponent = ({
  currentLedgerEntry,
  getLedgerEntry,
  translate,
  updateLedgerEntryHandler,
  deleteLedgerEntryHandler,
}) => {
  const [leOriginal, setLeOriginal] = useState({});
  const [leRestRep, setLeRestRep] = useState(leOriginal);
  const [updateRequest, setUpdateRequest] = useState(false);
  const [deleteRequest, setDeleteRequest] = useState(false);

  const { id: idParam } = useParams();
  const id = Number(idParam);
  useEffect(() => {
    if (currentLedgerEntry !== null && currentLedgerEntry.id === Number(id)) {
      // alles geladen
      setLeOriginal(currentLedgerEntry);
      setLeRestRep(currentLedgerEntry);
    } else {
      getLedgerEntry(id);
    }
    setUpdateRequest(false);
  }, [currentLedgerEntry, getLedgerEntry, id]);

  const handleTextChange = name => event => {
    setLeRestRep({ ...leRestRep, [name]: event.target.value });
  };

  const handleSelectChange = name => event => {
    setLeRestRep({
      ...leRestRep,
      [name]: getKeyFromSelectedValue(LedgerValues[name], event.target.value),
    });
  };

  const handleDateChange = (name, date) => {
    setLeRestRep({ ...leRestRep, [name]: moment.utc(date).valueOf() });
  };

  const customStyle = theme => ({
    formControl: {
      marginTop: "16px",
      flexGrow: 1,
    },
  });
  const theme = useTheme();
  const sx = useMemo(
    () => ({
      paper: {
        p: theme.spacing(3, 2),
        my: theme.spacing(1, 0),
      },
      container: {
        display: "flex",
        flexWrap: "wrap",
      },
      textField: {
        ml: 1,
        mr: 1,
      },
      readOnlyPadding: {
        pt: 1,
      },
      lineExpander: {
        flexGrow: 1,
      },
      lineBreak: {
        width: "100%",
      },
      title: {
        flex: "0 0 auto",
        mb: 2,
        ml: 1,
      },
      inLine: {
        width: "100%",
        display: "inline-flex",
        mb: 1,
      },
      spaceButtons: {
        flex: 1,
        width: "10px",
      },
      button: {
        m: theme.spacing(0, 1),
        width: 100,
      },
      modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      progress: {
        m: theme.spacing(2),
      },
    }),
    [theme]
  );
  return deleteRequest || updateRequest ? (
    <Navigate to={BILLING_LEDGER_ENTRIES_PATH} replace />
  ) : (
    [
      <Modal
        key="update-modal"
        open={updateRequest}
        sx={sx.modal}
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={updateRequest}>
          <CircularProgress sx={sx.progress} style={{ userSelect: "none" }} />
        </Fade>
      </Modal>,
      <Paper key={`billing-data-${leRestRep.id || ""}-${updateRequest}`} sx={sx.paper}>
        <Box sx={sx.inLine}>
          <Box sx={sx.title}>
            <Typography variant="h6" id="tableTitle">
              {translate.billingData}
            </Typography>
          </Box>
          <Box sx={sx.spaceButtons} />
          <Button
            key="ledger_entries_btn_save"
            variant="contained"
            size="small"
            sx={sx.button}
            onClick={() => {
              updateLedgerEntryHandler(id, difference(leRestRep, leOriginal));
              setUpdateRequest(true);
            }}
          >
            <SaveIcon sx={{ mr: 1, fontSize: 20 }} />
            {translate.saveButton}
          </Button>
          <Button
            key="ledger_entries_btn_del"
            variant="contained"
            color="secondary"
            sx={sx.button}
            onClick={() => {
              if (window.confirm("Are you sure to delete this entry?")) {
                deleteLedgerEntryHandler(id);
                setDeleteRequest(true);
              }
            }}
          >
            {translate.deleteButton}
            <DeleteIcon sx={{ ml: 1, fontSize: 20 }} />
          </Button>
        </Box>
        <Box component="form" key={leRestRep.id} sx={sx.container} noValidate autoComplete="off">
          <TextField
            id="ledger-entry-id"
            label="Id"
            sx={[sx.textField, sx.lineExpander]}
            style={{ "--background-color": "#ffffff" }}
            defaultValue={leRestRep.id || ""}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <TextField
            id="ledger-entry-billing-group"
            label={translate.billingGroup}
            sx={[sx.textField, sx.lineExpander, sx.readOnlyPadding]}
            value={leRestRep.billingGroup || ""}
            margin="normal"
            onChange={handleTextChange("billingGroup")}
          />
          <TextField
            id="ledger-entry-customer_billing_reference"
            label={translate.customerBillingReference}
            sx={[sx.textField, sx.lineExpander, sx.readOnlyPadding]}
            value={leRestRep.customerBillingReference || ""}
            margin="normal"
            onChange={handleTextChange("customerBillingReference")}
          />
          <Box sx={sx.lineBreak} />
          <DateTimePicker
            ampm={false}
            format="DD.MM.YYYY HH:mm:ss"
            value={leRestRep.startingTime ? moment(leRestRep.startingTime) : null}
            onChange={date => handleDateChange("startingTime", date)}
            slotProps={{
              textField: {
                id: "ledger-entry-starting-time",
                label: translate.billingLedgerParticipantStartingTime,
                sx: [sx.textField, sx.lineExpander],
                margin: "normal",
              },
            }}
          />
          <DateTimePicker
            ampm={false}
            format="DD.MM.YYYY HH:mm:ss"
            value={leRestRep.endingTime ? moment(leRestRep.endingTime) : null}
            onChange={date => handleDateChange("endingTime", date)}
            slotProps={{
              textField: {
                id: "ledger-entry-ending-time",
                label: translate.billingLedgerParticipantEndingTime,
                sx: [sx.textField, sx.lineExpander],
                margin: "normal",
              },
            }}
          />
          <Box sx={sx.lineBreak} />
          <TextField
            id="ledger-entry-quantity"
            label={translate.quantity}
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.quantity || ""}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <TextField
            id="ledger-entry-quantity-adjusted"
            label={translate.quantityAdjust}
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.quantityAdjusted || ""}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <Box sx={sx.lineBreak} />
          <TextField
            id="ledger-entry-stat"
            label="Status"
            sx={[sx.textField, sx.lineExpander]}
            defaultValue={leRestRep.stat}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <TextField
            id="ledger-entry-error-code"
            label={translate.errorCodeLedgerEntry}
            sx={[sx.textField, sx.lineExpander]}
            defaultValue={leRestRep.errorCode}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <TextField
            id="ledger-entry-call-typ"
            label="Typ"
            sx={[sx.textField, sx.lineExpander]}
            defaultValue={leRestRep.typ}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
        </Box>
      </Paper>,
      <Paper key={`personal-data-${leRestRep.id || ""}`} sx={sx.paper}>
        <Box sx={sx.title}>
          <Typography variant="h6" id="tableTitle">
            {translate.personalData}
          </Typography>
        </Box>
        <Box component="form" key={leRestRep.id} sx={sx.container} noValidate autoComplete="off">
          <TextField
            id="ledger-entry-name"
            label={translate.name}
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.name || ""}
            margin="normal"
            onChange={handleTextChange("name")}
          />
          <TextField
            id="ledger-entry-user-name"
            label={translate.participantName}
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.userName || ""}
            margin="normal"
            onChange={handleTextChange("userName")}
          />
          <TextField
            id="ledger-entry-request-from"
            label={translate.requestFrom}
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.requestFrom || ""}
            margin="normal"
            onChange={handleTextChange("requestFrom")}
          />
          <Box sx={sx.lineBreak} />
          <TextField
            id="ledger-entry-e-mail"
            label="eMail"
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.eMail || ""}
            margin="normal"
            onChange={handleTextChange("eMail")}
          />
          <TextField
            id="ledger-entry-country"
            label={translate.country}
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.country || ""}
            margin="normal"
            onChange={handleTextChange("country")}
          />
        </Box>
      </Paper>,
      <Paper key={`product-data-${leRestRep.id || ""}`} sx={sx.paper}>
        <Box sx={sx.title}>
          <Typography variant="h6" id="tableTitle">
            {translate.productData}
          </Typography>
        </Box>
        <Box component="form" key={leRestRep.id} sx={sx.container} noValidate autoComplete="off">
          <TextField
            id="ledger-entry-product-no"
            label={translate.productNumber}
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.productNo || ""}
            margin="normal"
            onChange={handleTextChange("productNo")}
          />
          <TextField
            id="ledger-entry-contract-no"
            label={translate.contractNumber}
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.contractNo || ""}
            margin="normal"
            onChange={handleTextChange("contractNo")}
          />
          <TextField
            id="ledger-entry-evn-dial-out-structure"
            label={translate.evnDialOutStructure}
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.evnDialOutStructure || ""}
            margin="normal"
            onChange={handleTextChange("evnDialOutStructure")}
          />
          <Box sx={sx.lineBreak} />
          <Box sx={sx.lineBreak} />
          <TextField
            id="ledger-entry-conference-id"
            label={translate.conferenceId}
            sx={[sx.textField, sx.lineExpander]}
            defaultValue={leRestRep.conferenceId}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <TextField
            id="ledger-entry-room-id"
            label={translate.passcodeConferenceRoomId}
            sx={[sx.textField, sx.lineExpander]}
            defaultValue={leRestRep.roomId}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <TextField
            id="ledger-entry-call-detail-id"
            label={translate.callDetailId}
            sx={[sx.textField, sx.lineExpander]}
            defaultValue={leRestRep.callDetailId}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <Box sx={sx.lineBreak} />
          <TextField
            id="ledger-entry-pool-conf-id"
            label={translate.poolConfId}
            sx={[sx.textField, sx.lineExpander]}
            defaultValue={leRestRep.poolConfId}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <TextField
            id="ledger-entry-pool-part-id"
            label={translate.poolPartId}
            sx={[sx.textField, sx.lineExpander]}
            defaultValue={leRestRep.poolPartId}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <TextField
            id="ledger-entry-call-product"
            label={translate.product}
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.product || ""}
            margin="normal"
            onChange={handleTextChange("product")}
          />
          <Box sx={sx.lineBreak} />
          <TextField
            id="ledger-entry-A-Phone-No"
            label={translate.aPhoneNumber}
            sx={[sx.textField, sx.lineExpander]}
            defaultValue={leRestRep.aPhoneNo}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <TextField
            id="ledger-entry-B-Phone-No"
            label={translate.bPhoneNumber}
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.bPhoneNo || ""}
            margin="normal"
            onChange={handleTextChange("bPhoneNo")}
          />
          <NativeSelect
            customStyle={customStyle}
            value={translate.netType}
            selectValues={getSelectValues(LedgerValues, "nettype")}
            defaultSelect={getSelectedValueTranslated(leRestRep.nettype, LedgerValues, "nettype") || "Festnetz"}
            onChange={handleSelectChange("nettype")}
          />
          <Box sx={sx.lineBreak} />
          <TextField
            id="ledger-entry-billingPoint"
            label={translate.billingPoint}
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.billingPoint || ""}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <TextField
            id="ledger-entry-billingTime"
            label={translate.billingTime}
            sx={[sx.textField, sx.lineExpander]}
            value={leRestRep.billingTime || ""}
            margin="normal"
            variant="filled"
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <Box sx={sx.lineBreak} />
        </Box>
      </Paper>,
    ]
  );
};

BillingLedgerEntryComponent.defaultProps = {
  currentLedgerEntry: {},
};

BillingLedgerEntryComponent.propTypes = {
  currentLedgerEntry: PropTypes.object,
  translate: PropTypes.object.isRequired,
  getLedgerEntry: PropTypes.func.isRequired,
  updateLedgerEntryHandler: PropTypes.func.isRequired,
  deleteLedgerEntryHandler: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  currentLedgerEntry: state.billing.currentLedgerEntry,
  translate: state.ui.translate,
});

const mapDispatchToProps = dispatch => ({
  getLedgerEntry: id => dispatch(requestLedgerEntry(id)),
  updateLedgerEntryHandler: (id, ledgerEntry) => dispatch(updateLedgerEntry(id, ledgerEntry)),
  deleteLedgerEntryHandler: id => dispatch(deleteLedgerEntry(id)),
});

const BillingLedgerEntry = connect(
  mapStateToProps,
  mapDispatchToProps
)(BillingLedgerEntryComponent);

export default BillingLedgerEntry;
